import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { canonAPI } from '../lib/api';
import { 
  BookOpen, 
  Search,
  XCircle,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';

export default function GlossaryPage() {
  const [glossary, setGlossary] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadGlossary() {
      try {
        const response = await canonAPI.getGlossary();
        setGlossary(response.data);
      } catch (error) {
        console.error('Failed to load glossary:', error);
      } finally {
        setLoading(false);
      }
    }
    loadGlossary();
  }, []);

  const filteredGlossary = glossary.filter(term => 
    term.term.toLowerCase().includes(search.toLowerCase()) ||
    term.definition.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8" data-testid="glossary-loading">
        <div className="space-y-4">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="loading-pulse h-32 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="glossary-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
          <BookOpen className="h-4 w-4" />
          <span>Part VIII — Article 25</span>
        </div>
        <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          Glossary v1.0
        </h1>
        <p className="text-muted-foreground">
          Precise, neutral definitions — critical for AI consistency and legal clarity
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search terms..."
          className="pl-10"
          data-testid="glossary-search"
        />
      </div>

      {/* Glossary List */}
      <div className="space-y-4" data-testid="glossary-list">
        {filteredGlossary.map((item) => (
          <Card key={item.term} className="glossary-term" data-testid={`term-${item.term.toLowerCase().replace(/\s/g, '-')}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">{item.term}</CardTitle>
                {item.status === 'prohibited' && (
                  <Badge className="bg-red-500/20 text-red-700 dark:text-red-400">
                    <XCircle className="h-3 w-3 mr-1" />
                    Prohibited
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Main Definition */}
              <p className="text-muted-foreground leading-relaxed">{item.definition}</p>
              
              {/* Excludes */}
              {item.excludes && item.excludes.length > 0 && (
                <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                  <h4 className="text-sm font-medium mb-2 flex items-center text-red-700 dark:text-red-400">
                    <XCircle className="h-4 w-4 mr-1" />
                    Does NOT Include
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {item.excludes.map((exc, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-red-500 mr-2">×</span>
                        {exc}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Requirements */}
              {item.requirements && item.requirements.length > 0 && (
                <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20">
                  <h4 className="text-sm font-medium mb-2 flex items-center text-green-700 dark:text-green-400">
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Requirements
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {item.requirements.map((req, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Examples */}
              {item.examples && item.examples.length > 0 && (
                <div className="p-3 rounded-lg bg-muted/50">
                  <h4 className="text-sm font-medium mb-2">Examples</h4>
                  <div className="flex flex-wrap gap-2">
                    {item.examples.map((ex, i) => (
                      <Badge key={i} variant="secondary">{ex}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Measures */}
              {item.measures && item.measures.length > 0 && (
                <div className="p-3 rounded-lg bg-muted/50">
                  <h4 className="text-sm font-medium mb-2">Measured By</h4>
                  <div className="flex flex-wrap gap-2">
                    {item.measures.map((m, i) => (
                      <Badge key={i} variant="outline">{m}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Related Articles */}
              {item.related_articles?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Related Articles:</span>
                  {item.related_articles.map((num) => (
                    <Link
                      key={num}
                      to={`/article/${num}`}
                      className="text-accent hover:underline"
                    >
                      Art. {num}
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredGlossary.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p>No terms found matching "{search}"</p>
        </div>
      )}

      {/* Important Note */}
      <Card className="mt-8 border-accent/50 bg-accent/5">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-accent mt-0.5" />
            <div>
              <h4 className="font-medium">Why Precise Definitions Matter</h4>
              <p className="text-sm text-muted-foreground">
                Vague terms enable abuse. Each definition in this glossary has a precise, narrow meaning 
                that cannot be stretched beyond its boundaries. AI systems must use these definitions exactly 
                as written. This ensures consistent, predictable interpretation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
